import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import TemplateEditor from "@/components/TemplateEditor";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, UploadCloud, FileCheck2, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
// Phase A1 follow-up — AI generator panel available on the NEW-template
// (Builder) creation flow too, not just the edit page.
import TemplateAIGeneratorPanel from "@/components/TemplateAIGeneratorPanel";

function UploadMode({ onCreated, templateType }) {
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [description, setDescription] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 25 * 1024 * 1024) {
            toast.error("File exceeds 25 MB");
            return;
        }
        setFile(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return toast.error("Template name is required");
        if (!file) return toast.error("Please choose a PDF or DOCX file");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("name", name.trim());
            if (category.trim()) fd.append("category", category.trim());
            if (description.trim()) fd.append("description", description.trim());
            // Phase H.4 — page-locked type so this dialog can never create a
            // mismatched template (server enforces it too).
            fd.append("template_type", templateType);
            fd.append("file", file);
            const { data } = await api.post("/admin/templates/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Template uploaded");
            onCreated(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="max-w-2xl space-y-5" data-testid="upload-template-form">
            <Card>
                <CardContent className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="up_name">Template name</Label>
                            <Input id="up_name" data-testid="upload-name-input" value={name} onChange={(e) => setName(e.target.value)} required />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="up_cat">Category</Label>
                            <Input id="up_cat" data-testid="upload-category-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="lease, addendum, …" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="up_desc">Description</Label>
                        <Input id="up_desc" data-testid="upload-description-input" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                    <div>
                        <Label className="mb-2 block">File (PDF or DOCX, ≤ 25 MB)</Label>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={onPickFile}
                            data-testid="upload-file-input"
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            data-testid="upload-dropzone"
                            className="w-full border-2 border-dashed border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 transition-colors rounded-md py-8 px-6 text-center"
                        >
                            {file ? (
                                <div className="flex items-center justify-center gap-3">
                                    <FileCheck2 className="text-emerald-700" size={22} />
                                    <div className="text-left">
                                        <div className="font-medium text-sm text-slate-900">{file.name}</div>
                                        <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <UploadCloud className="mx-auto text-slate-400 mb-2" size={24} />
                                    <div className="font-medium text-sm text-slate-900">Click to choose a file</div>
                                    <div className="text-xs text-slate-500 mt-0.5">PDF or DOCX, up to 25 MB</div>
                                </>
                            )}
                        </button>
                    </div>
                </CardContent>
            </Card>
            <div className="flex justify-end gap-2">
                <Button type="button" asChild variant="outline">
                    <Link to={templateType === "ach_authorization" ? "/admin/templates/ach" : "/admin/templates/contracts"} data-testid="upload-cancel">Cancel</Link>
                </Button>
                <Button type="submit" disabled={busy} data-testid="upload-submit-button" className="bg-blue-900 hover:bg-blue-800">
                    {busy ? "Uploading…" : "Upload template"}
                </Button>
            </div>
        </form>
    );
}

function UploadSuccess({ template, listRoute = "/admin/templates/contracts" }) {
    return (
        <Card className="max-w-2xl mt-6" data-testid="upload-success">
            <CardContent className="p-6">
                <div className="inline-flex items-center gap-2 text-emerald-700 mb-2">
                    <CheckCircle2 size={18} />
                    <span className="text-xs font-medium tracking-[0.18em] uppercase">Template uploaded</span>
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{template.name}</h2>
                <p className="text-sm text-slate-500 mt-1">{template.original_filename}</p>

                <div className="mt-5 space-y-3 text-sm">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Detected tags ({template.detected_tags.length})</div>
                        <div className="flex flex-wrap gap-1">
                            {template.detected_tags.length === 0 && <span className="text-slate-400 text-xs">None — make sure your file uses {"{{double-curly}}"} tags.</span>}
                            {template.detected_tags.map((t) => (
                                <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-100 font-mono">{t}</span>
                            ))}
                        </div>
                    </div>
                    {template.unknown_tags.length > 0 && (
                        <div>
                            <div className="text-xs uppercase tracking-wider text-red-700 mb-1 inline-flex items-center gap-1">
                                <AlertTriangle size={12} /> Unknown tags ({template.unknown_tags.length})
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {template.unknown_tags.map((t) => (
                                    <span key={t} data-testid={`upload-unknown-${t}`} className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100 font-mono">{t}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6 flex gap-2">
                    <Button asChild className="bg-blue-900 hover:bg-blue-800">
                        <Link to={`/admin/templates/${template.id}`} data-testid="upload-success-open">Open template</Link>
                    </Button>
                    <Button asChild variant="outline">
                        <Link to={listRoute}>Back to list</Link>
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

export default function NewTemplatePage({ templateType = "rental_agreement" }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialMode = searchParams.get("mode") === "upload" ? "upload" : "builder";
    const [mode, setMode] = useState(initialMode);
    const [createdUpload, setCreatedUpload] = useState(null);
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    // Phase A1 follow-up — AI generator panel state for the Builder
    // creation flow. Mirrors the edit-page wiring (editorRef +
    // applyGeneratedBody + generatorEnabled fetch).
    const [generatorOpen, setGeneratorOpen] = useState(false);
    const [generatorEnabled, setGeneratorEnabled] = useState(true);
    const editorRef = useRef(null);

    useEffect(() => {
        if (!isSuperAdmin) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/system-settings");
                setGeneratorEnabled(data?.template_generator_enabled !== false);
            } catch (_) { /* default to enabled */ }
        })();
    }, [isSuperAdmin]);

    const applyGeneratedBody = (bodyHtml) => {
        if (!editorRef.current?.applyRawBodyHtml) {
            toast.error("Editor not ready — refresh and try again.");
            return;
        }
        editorRef.current.applyRawBodyHtml(bodyHtml);
    };

    // Phase H.4 — same component, two filtered pages.
    const isAch = templateType === "ach_authorization";
    const isInvoice = templateType === "invoice";
    const listRoute = isAch
        ? "/admin/templates/ach"
        : isInvoice
        ? "/admin/templates/invoices"
        : "/admin/templates/contracts";
    const pageHeading = isAch
        ? "Create an ACH Authorization template"
        : isInvoice
        ? "Create an invoice template"
        : "Create a contract template";
    const subhead = isAch
        ? "Build the ACH bank-debit authorization customers will sign alongside their rental agreement, or upload a vetted PDF/DOCX."
        : isInvoice
        ? "Build the invoice layout customers will receive, or upload an existing PDF/DOCX file containing {{merge tags}}."
        : "Build from scratch in the editor or upload an existing PDF/DOCX file containing {{merge tags}}.";

    const onBuilderSave = async (payload) => {
        setSaving(true);
        try {
            // Phase H.4 — page-locked type so this dialog can never create a
            // mismatched template (server enforces it too).
            const { data } = await api.post("/admin/templates", { ...payload, template_type: templateType });
            toast.success("Template created");
            navigate(`/admin/templates/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <DashboardShell>
            <div className="fade-up">
                <Link to={listRoute} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3">
                    <ChevronLeft size={14} /> Templates
                </Link>
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">New template</p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                    {pageHeading}
                </h1>
                <p className="mt-2 text-sm text-slate-600">
                    {subhead}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-white" role="tablist">
                        <button
                            type="button"
                            data-testid="mode-builder"
                            onClick={() => { setMode("builder"); setCreatedUpload(null); }}
                            className={`px-4 py-1.5 text-sm font-medium rounded ${mode === "builder" ? "bg-blue-900 text-white" : "text-slate-600"}`}
                        >
                            Build in editor
                        </button>
                        <button
                            type="button"
                            data-testid="mode-upload"
                            onClick={() => { setMode("upload"); }}
                            className={`px-4 py-1.5 text-sm font-medium rounded ${mode === "upload" ? "bg-blue-900 text-white" : "text-slate-600"}`}
                        >
                            Upload PDF/DOCX
                        </button>
                    </div>
                    {/* Phase A1 follow-up — AI generator button on the
                        Builder creation flow (not just edit). Super-admin
                        + supported template types only; hidden when the
                        user is on the "Upload PDF/DOCX" sub-flow because
                        the upload path itself can route through the
                        upload-AI endpoint and the editor isn't mounted. */}
                    {isSuperAdmin
                        && mode === "builder"
                        && ["invoice", "rental_agreement", "ach_authorization"].includes(templateType) && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setGeneratorOpen(true)}
                            data-testid="new-template-generate-ai-btn"
                            className="gap-1.5 border-blue-200 text-blue-900 hover:bg-blue-50"
                        >
                            <Sparkles size={14} /> Generate with AI
                        </Button>
                    )}
                </div>
            </div>

            <div className="mt-6">
                {mode === "builder" ? (
                    <TemplateEditor
                        ref={editorRef}
                        onSave={onBuilderSave}
                        saving={saving}
                        submitLabel="Create template"
                    />
                ) : createdUpload ? (
                    <UploadSuccess template={createdUpload} listRoute={listRoute} />
                ) : (
                    <UploadMode onCreated={setCreatedUpload} templateType={templateType} />
                )}
            </div>

            {/* Phase A1 follow-up — AI generator dialog on the New-template
                Builder page. The panel doesn't need a saved template ID
                to operate; applying the generated HTML populates the
                editor in-memory and the user explicitly clicks "Create
                template" to persist. */}
            {isSuperAdmin
                && ["invoice", "rental_agreement", "ach_authorization"].includes(templateType) && (
                <TemplateAIGeneratorPanel
                    open={generatorOpen}
                    onOpenChange={setGeneratorOpen}
                    templateType={templateType}
                    initialName=""
                    onApply={applyGeneratedBody}
                    generatorEnabled={generatorEnabled}
                />
            )}
        </DashboardShell>
    );
}
